export const formatDateKey = (date) => {
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

export const dateFromKey = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || "")) return null;
  const [year, month, day] = value.split("-").map((part) => parseInt(part, 10));
  const date = new Date(year, month - 1, day);
  return formatDateKey(date) === value ? date : null;
};

export const appointmentDateKey = (appointment) =>
  appointment.occurrence_date || appointment.date || null;

export const appointmentStableKey = (appointment, index) =>
  appointment.occurrence_key || appointment.id || `appointment-${index}`;

export const appointmentClient = (appointment) =>
  appointment.client_name || appointment.todo_client_name || "Client not provided";

export const appointmentClientNumber = (appointment) =>
  appointment.client_number ||
  appointment.resolved_client_id ||
  appointment.client_id ||
  "Not specified";

export const appointmentDestination = (appointment) =>
  appointment.destination || appointment.location || appointment.todo_location || "Not specified";

export const appointmentPickup = (appointment) =>
  appointment.pickup_location || appointment.todo_pickup_location || "Not specified";

export const appointmentUnit = (appointment) =>
  appointment.unit_location || appointment.resolved_unit_location || "Not specified";

export const appointmentMatchedPeer = (appointment) => {
  if (appointment.matched_peer_name) return appointment.matched_peer_name;
  const name = [appointment.first_name, appointment.last_name].filter(Boolean).join(" ");
  return name || "No Peer assigned";
};

export const appointmentRecurrence = (appointment) => {
  if (appointment.recurrence_label) return appointment.recurrence_label;
  if (appointment.recurrence) return appointment.recurrence;
  if (appointment.expandable_series || appointment.is_recurring || appointment.recurring)
    return "Recurring series";
  return "One-time appointment";
};

export const appointmentTime = (appointment) => {
  if (appointment.whole_day_reservation) return "Whole day";
  if (appointment.time_display || appointment.time_string)
    return appointment.time_display || appointment.time_string;
  if (!appointment.time) return "Time not provided";

  const parsedTime = new Date(appointment.time);
  if (Number.isNaN(parsedTime.getTime())) return "Time not provided";
  return parsedTime.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
};

export const compactAppointmentTime = (appointment) => {
  if (appointment.whole_day_reservation) return "All day";
  const displayTime = appointmentTime(appointment);
  const meridiemTime = displayTime.match(/^(\d{1,2}):(\d{2})\s*([ap]m)$/i);
  if (meridiemTime) {
    let hours = parseInt(meridiemTime[1], 10) % 12;
    if (meridiemTime[3].toLowerCase() === "pm") hours += 12;
    return `${String(hours).padStart(2, "0")}:${meridiemTime[2]}`;
  }

  const twentyFourHourTime = displayTime.match(/^(\d{1,2}:\d{2})/);
  return twentyFourHourTime ? twentyFourHourTime[1].padStart(5, "0") : displayTime;
};

export const appointmentTimestamp = (appointment) => {
  if (appointment.whole_day_reservation) return -1;
  const displayTime = appointment.time_display || appointment.time_string;
  if (displayTime && /^\d{1,2}:\d{2}/.test(displayTime)) {
    const [hours, minutes] = displayTime.split(":").map((part) => parseInt(part, 10));
    return hours * 60 + minutes;
  }
  const parsedTime = new Date(appointment.time);
  return Number.isNaN(parsedTime.getTime())
    ? Number.MAX_SAFE_INTEGER
    : parsedTime.getHours() * 60 + parsedTime.getMinutes();
};

export const sortAppointments = (appointments) =>
  [...appointments].sort((first, second) => {
    if (first.whole_day_reservation !== second.whole_day_reservation)
      return first.whole_day_reservation ? -1 : 1;
    return appointmentTimestamp(first) - appointmentTimestamp(second);
  });

export const hasTimeOffOnDay = (peerSchedules, day) => {
  if (!Array.isArray(peerSchedules) || peerSchedules.length === 0) return false;
  const dayOfWeek = day.getDay();

  return peerSchedules.some((peerSchedule) => {
    const startDate = dateFromKey(peerSchedule.start_date);
    const endDate = peerSchedule.end_date ? dateFromKey(peerSchedule.end_date) : null;
    if (!startDate || day < startDate || (endDate && day > endDate)) return false;

    switch (peerSchedule.recurring) {
      case "ever_day":
        return true;
      case "weekdays":
        return dayOfWeek >= 1 && dayOfWeek <= 5;
      case "weekends":
        return dayOfWeek === 0 || dayOfWeek === 6;
      case "mon":
        return dayOfWeek === 1;
      case "tue":
        return dayOfWeek === 2;
      case "wed":
        return dayOfWeek === 3;
      case "thu":
        return dayOfWeek === 4;
      case "fri":
        return dayOfWeek === 5;
      case "sat":
        return dayOfWeek === 6;
      case "sun":
        return dayOfWeek === 0;
      default:
        return formatDateKey(startDate) === formatDateKey(day);
    }
  });
};
